import { CreditCardOutlined, StarOutlineRounded, StarRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import { useState } from "react";
import AddPaymentMethodModal from "@/components/usersOrgs/AddPaymentMethodModal";
import {
  useAwaitAddedPaymentMethod,
  usePaymentMethods,
  useRemovePaymentMethod,
  useSetDefaultPaymentMethod,
} from "@/hooks/usePaymentMethods";
import { usePermissions } from "@/hooks/usePermissions";
import type { PaymentMethod } from "@/interfaces/paymentMethod";

const cardSx = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  px: 2.5,
  py: 1.75,
  border: "1px solid var(--color-card-border)",
  borderRadius: "12px",
  bgcolor: "background.paper",
  "&:hover .pm-actions": { opacity: 1 },
} as const;

const defaultBadge = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  padding: "2px 8px",
  borderRadius: 9999,
  background: "var(--color-good-sage-4)",
  color: "var(--color-good-sage)",
  whiteSpace: "nowrap" as const,
};

function expLabel(m: number | null, y: number | null): string {
  if (!m || !y) return "No expiry on file";
  return `Expires ${String(m).padStart(2, "0")}/${y}`;
}

export default function GroupPaymentMethodsPanel({
  groupId,
  canPurchase,
}: {
  groupId: number;
  canPurchase: boolean;
}) {
  const { data: cards = [], isLoading } = usePaymentMethods(groupId);
  const setDefault = useSetDefaultPaymentMethod(groupId);
  const remove = useRemovePaymentMethod(groupId);
  const { can } = usePermissions();
  const canManageCards = can("buy", "CREDIT");
  // Adding a card requires the group to be purchase-enabled (the flag governs card-on-file too).
  // Managing existing cards (default/remove) stays on the CREDIT/buy capability, so cards survive the
  // flag flipping off.
  const canAdd = canManageCards && canPurchase;
  const awaitAddedCard = useAwaitAddedPaymentMethod(groupId);

  const [addOpen, setAddOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // After a card is added, its row is written by the setup_intent.succeeded WEBHOOK, which can
  // lag the modal close by a moment — `useAwaitAddedPaymentMethod` invalidates and then polls
  // until it lands (design §2.5a). The campaign-builder checkout waits the same way.
  function handleAdded() {
    setError(null);
    awaitAddedCard();
  }

  function handleSetDefault(card: PaymentMethod) {
    setDefault.mutate(card.id, {
      onError: (e) => setError(e instanceof Error ? e.message : "Failed to set default."),
    });
  }

  function handleRemove(card: PaymentMethod) {
    if (!window.confirm(`Remove the ${card.brand ?? "card"} ending ${card.last4 ?? "••••"}?`))
      return;
    remove.mutate(card.id, {
      onError: (e) => setError(e instanceof Error ? e.message : "Failed to remove card."),
    });
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 20 }}>
            Payment methods
          </Typography>
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            Cards on file for this group, saved securely with Stripe.
          </Typography>
        </Box>
        {canAdd && cards.length > 0 && (
          <Button
            variant="contained"
            onClick={() => setAddOpen(true)}
            sx={{ bgcolor: "var(--color-rodeo-black)", whiteSpace: "nowrap" }}
          >
            Add payment method
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress aria-label="Loading" size={22} />
        </Box>
      ) : cards.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 3,
            border: "1px dashed var(--color-smoke)",
            borderRadius: "12px",
          }}
        >
          <CreditCardOutlined sx={{ fontSize: 40, color: "var(--color-dark-dust)", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>No payment methods yet</Typography>
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mb: 2 }}>
            {canPurchase
              ? "Add a card to purchase gift credits. Your details are stored securely with Stripe."
              : "This group isn't enabled to purchase credits, so cards can't be added."}
          </Typography>
          {canAdd && (
            <Button
              variant="contained"
              onClick={() => setAddOpen(true)}
              sx={{ bgcolor: "var(--color-rodeo-black)" }}
            >
              Add payment method
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {cards.map((card) => (
            <Box key={card.id} sx={cardSx}>
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "var(--color-rodeo-black)",
                  minWidth: 64,
                }}
              >
                {card.brand ?? "Card"}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  •••• {card.last4 ?? "••••"}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
                  {expLabel(card.expMonth, card.expYear)}
                  {card.label ? ` · ${card.label}` : ""}
                </Typography>
              </Box>

              {card.isDefault && <span style={defaultBadge}>Default</span>}

              {canManageCards && (
                <Box
                  className="pm-actions"
                  sx={{
                    display: "flex",
                    gap: 0.25,
                    opacity: 0,
                    transition: "opacity 0.12s",
                  }}
                >
                  {!card.isDefault && (
                    <IconButton
                      size="small"
                      title="Set as default"
                      disabled={setDefault.isPending}
                      onClick={() => handleSetDefault(card)}
                    >
                      <StarOutlineRounded sx={{ fontSize: 18, color: "var(--color-dark-dust)" }} />
                    </IconButton>
                  )}
                  {card.isDefault && (
                    <StarRounded sx={{ fontSize: 18, color: "var(--color-good-sage)", m: "5px" }} />
                  )}
                  <Button
                    size="small"
                    disabled={remove.isPending}
                    onClick={() => handleRemove(card)}
                    sx={{ color: "var(--color-good-wine)", minWidth: 0 }}
                  >
                    Remove
                  </Button>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      <AddPaymentMethodModal
        open={addOpen}
        groupId={groupId}
        onClose={() => setAddOpen(false)}
        onAdded={handleAdded}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)} variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
